import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import HeaderShopSeller from "../../Layout/headerShopSeller";
import PageBuilder from "../../pageInside/PageBuilder/PageBuilder";
import PageRenderer from "../../pageInside/PageBuilder/PageRenderer";
import { isAuthenticated } from "../../auth";
import Layout from "../../Layout/Layout";
import { getUserAbout, saveUserAbout } from "../../auth/apiUserAbout";
import { getPublicShop } from "../../auth/apiShops";
import { elakayAutoText } from "../../pageInside/dataElakay";
import { getPublicIdentifier } from "../../../utils/publicIdentifier";


import { makeNodeId, aboutPlaceholderImg, aboutCard, aboutHeading, aboutText, aboutImage, aboutGalleryRow, aboutBadgeGrid } from "../shop/aboutDocKit";

const makeStarterAboutDoc = (owner, legacyText = "", boutique = null) => {
  const shopName = boutique?.name || owner?.shopName || owner?.firstname || "Our Shop";
  const ownerId = owner?.publicId ? String(owner.publicId) : owner?._id ? String(owner._id) : "";
  const shopHref = ownerId ? `/lakay-ShopSeller/${ownerId}${boutique?._id ? `?shop=${boutique._id}` : ""}` : "#";
  const pageId = makeNodeId("about-page");

  const advantages = [
    { icon: "🏅", title: elakayAutoText("h_about_adv1_title", "Quality Products"), text: elakayAutoText("h_about_adv1_text", "We carefully select high quality products") },
    { icon: "💰", title: elakayAutoText("h_about_adv2_title", "Best Prices"), text: elakayAutoText("h_about_adv2_text", "Competitive prices and great value") },
    { icon: "🚚", title: elakayAutoText("h_about_adv3_title", "Fast Shipping"), text: elakayAutoText("h_about_adv3_text", "Quick and reliable delivery") },
    { icon: "🔒", title: elakayAutoText("h_about_adv4_title", "Secure Payment"), text: elakayAutoText("h_about_adv4_text", "Safe and secure transactions") },
    { icon: "🎧", title: elakayAutoText("h_about_adv5_title", "24/7 Support"), text: elakayAutoText("h_about_adv5_text", "We are here to help you anytime") },
    { icon: "🔄", title: elakayAutoText("h_about_adv6_title", "Easy Returns"), text: elakayAutoText("h_about_adv6_text", "Hassle-free return process") },
    { icon: "🛡️", title: elakayAutoText("h_about_adv7_title", "Buyer Protection"), text: elakayAutoText("h_about_adv7_text", "Your satisfaction is our priority") },
    { icon: "⭐", title: elakayAutoText("h_about_adv8_title", "Trusted Seller"), text: elakayAutoText("h_about_adv8_text", "Verified and reliable shop on Elakay") },
  ];

  const values = [
    { icon: "🤝", title: elakayAutoText("h_about_val1_title", "Integrity"), text: elakayAutoText("h_about_val1_text", "We believe in honesty, fairness, and ethical business practices.") },
    { icon: "🙏", title: elakayAutoText("h_about_val2_title", "Respect"), text: elakayAutoText("h_about_val2_text", "We treat every customer with courtesy, professionalism, and appreciation.") },
    { icon: "✅", title: elakayAutoText("h_about_val3_title", "Reliability"), text: elakayAutoText("h_about_val3_text", "We strive to provide dependable service and consistent communication.") },
    { icon: "📋", title: elakayAutoText("h_about_val4_title", "Accountability"), text: elakayAutoText("h_about_val4_text", "We take responsibility for our actions and continuously seek opportunities to improve.") },
    { icon: "💡", title: elakayAutoText("h_about_val5_title", "Innovation"), text: elakayAutoText("h_about_val5_text", "We embrace new ideas, technologies, and processes that help improve customer experiences.") },
  ];

  const contactItems = [
    { icon: "📞", title: elakayAutoText("h_about_contact1_title", "Contact Us"), text: elakayAutoText("h_about_contact1_text", "Anytime") },
    { icon: "✉️", title: elakayAutoText("h_about_contact2_title", "Email Us"), text: elakayAutoText("h_about_contact2_text", "We reply fast") },
    { icon: "☎️", title: elakayAutoText("h_about_contact3_title", "Call Us"), text: elakayAutoText("h_about_contact3_text", "Mon - Sat, 9AM - 6PM") },
    { icon: "💬", title: elakayAutoText("h_about_contact4_title", "Live Chat"), text: elakayAutoText("h_about_contact4_text", "We are online") },
  ];

  const paymentMethods = ["VISA", "Mastercard", "PayPal", "Apple Pay", "Google Pay"];

  return {
    meta: {
      title: `About ${shopName}`,
      description: `Learn more about ${shopName}.`,
    },
    pageMode: "static",
    currentPageId: pageId,
    comments: [],
    combinedElements: [],
    pages: [
      {
        id: pageId,
        name: "About Us",
        style: {
          maxWidth: "1080px",
          margin: "0 auto",
          padding: "32px 16px 64px",
          fontFamily: "'DM Sans', system-ui, sans-serif",
          minHeight: "70vh",
        },
        children: [
          // ===== HERO / SHOP BANNER =====
          {
            id: makeNodeId("about-hero"),
            type: "container",
            style: {
              padding: "72px 36px",
              borderRadius: "24px",
              textAlign: "center",
              color: "#ffffff",
              background: `linear-gradient(135deg, rgba(7,89,133,0.92), rgba(109,40,217,0.92)), url(${aboutPlaceholderImg(1600, 520, "0f172a", "ffffff", "Shop Banner")}) center/cover no-repeat`,
              marginBottom: "24px",
            },
            children: [
              {
                id: makeNodeId("about-logo"),
                type: "image",
                src: aboutPlaceholderImg(160, 160, "ffffff", "6d28d9", shopName.slice(0, 1).toUpperCase()),
                alt: shopName,
                style: {
                  display: "block",
                  width: "112px",
                  height: "112px",
                  margin: "0 auto 20px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "4px solid rgba(255,255,255,0.85)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                },
              },
              aboutHeading(elakayAutoText("h_about_welcome_title", "Welcome to {value0}", { value0: shopName }), {
                tag: "h1", color: "#ffffff", size: "44px", align: "center", style: { margin: "0 0 12px" },
              }),
              aboutText(legacyText || elakayAutoText("h_about_tagline", "Quality products, great prices and excellent service."), {
                maxWidth: "680px", margin: "0 auto 26px", color: "#e0f2fe", fontSize: "18px", textAlign: "center",
              }),
              {
                id: makeNodeId("about-hero-badges"),
                type: "row",
                style: { display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "12px", marginBottom: "26px" },
                children: [
                  { icon: "🚚", text: elakayAutoText("h_about_badge1", "Fast Shipping") },
                  { icon: "🔒", text: elakayAutoText("h_about_badge2", "Secure Payment") },
                  { icon: "🎧", text: elakayAutoText("h_about_badge3", "24/7 Support") },
                  { icon: "🛡️", text: elakayAutoText("h_about_badge4", "Buyer Protection") },
                ].map((b) => ({
                  id: makeNodeId("about-hero-badge"),
                  type: "container",
                  style: {
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 16px",
                    borderRadius: "999px",
                    background: "rgba(255,255,255,0.12)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    color: "#ffffff",
                    fontSize: "13px",
                    fontWeight: "700",
                  },
                  children: [
                    { id: makeNodeId("about-hero-badge-icon"), type: "text", tag: "span", text: b.icon, style: { fontSize: "16px" } },
                    { id: makeNodeId("about-hero-badge-text"), type: "text", tag: "span", text: b.text, style: {} },
                  ],
                })),
              },
              {
                id: makeNodeId("about-cta"),
                type: "button",
                text: elakayAutoText("h_about_cta_visit", "Visit our shop"),
                href: shopHref,
                style: {
                  display: "inline-block",
                  padding: "13px 30px",
                  borderRadius: "999px",
                  background: "#ffffff",
                  color: "#075985",
                  fontWeight: "800",
                  fontSize: "14px",
                  letterSpacing: "0.02em",
                  textDecoration: "none",
                },
              },
            ],
          },

          // ===== INTRO =====
          aboutCard([
            aboutText(elakayAutoText("h_about_intro1", "Welcome to {value0} and thank you for visiting our store on Elakay.", { value0: shopName })),
            aboutText(elakayAutoText("h_about_intro2", "We are committed to providing quality products, professional service, and a reliable shopping experience for every customer. Our goal is to make online shopping simple, secure, and enjoyable while offering products that meet the needs and expectations of our customers.")),
            aboutText(elakayAutoText("h_about_intro3", "Our shop is part of the Elakay marketplace community, a platform dedicated to connecting buyers, sellers, producers, service providers, and entrepreneurs through a trusted and innovative marketplace.")),
            aboutText(elakayAutoText("h_about_intro4", "We understand that customers have many choices when shopping online. For that reason, we work hard to maintain high standards in product quality, customer support, communication, and order fulfillment. Every order represents an opportunity to earn your trust, and we take that responsibility seriously."), { margin: 0 }),
          ]),

          // ===== OUR STORY (gallery + mission + vision) =====
          aboutCard([
            aboutHeading(elakayAutoText("h_5a2d8eadc273c6", "Our Story")),
            aboutGalleryRow([
              elakayAutoText("h_about_gal_seller", "Seller / Owner Photo"),
              elakayAutoText("h_about_gal_team", "Our Team"),
              elakayAutoText("h_about_gal_story", "Our Story"),
            ], { ratio: "4 / 3" }),
            aboutHeading(elakayAutoText("h_about_mission_title", "Our Mission"), { tag: "h3", size: "20px" }),
            aboutText(elakayAutoText("h_about_mission_p1", "Our mission is to provide customers with access to quality products at fair and competitive prices while delivering exceptional customer service.")),
            aboutText(elakayAutoText("h_about_mission_list", "We believe that every customer deserves:\n• Honest product descriptions\n• Fair pricing\n• Secure transactions\n• Reliable communication\n• Fast and professional support\n• A positive shopping experience")),
            aboutText(elakayAutoText("h_about_mission_p2", "Our objective is not only to sell products but also to build long-term relationships based on trust, transparency, and customer satisfaction.")),
            aboutHeading(elakayAutoText("h_about_vision_title", "Our Vision"), { tag: "h3", size: "20px" }),
            aboutText(elakayAutoText("h_about_vision_p1", "Our vision is to become a trusted destination where customers can confidently discover products and services that improve their daily lives.")),
            aboutText(elakayAutoText("h_about_vision_p2", "We strive to create a shopping environment where buyers feel secure, informed, and valued throughout every stage of their purchasing journey.")),
            aboutText(elakayAutoText("h_about_vision_p3", "As our shop continues to grow, we remain focused on innovation, service excellence, and customer satisfaction."), { margin: 0 }),
          ]),

          // ===== WHAT WE OFFER =====
          aboutCard([
            aboutHeading(elakayAutoText("h_about_offer_title", "What We Offer")),
            aboutText(elakayAutoText("h_about_offer_p1", "Our shop offers a variety of products selected to meet the needs of different customers and communities.")),
            aboutText(elakayAutoText("h_about_offer_list", "Depending on our inventory and specialization, our products may include:\n• Consumer goods\n• Electronics and accessories\n• Fashion and apparel\n• Home and lifestyle products\n• Beauty and personal care items\n• Automotive products\n• Professional equipment\n• Handmade products\n• Digital services\n• Specialty items")),
            aboutText(elakayAutoText("h_about_offer_p2", "We continuously evaluate our product selection to ensure quality, value, and customer demand."), { margin: 0 }),
          ]),

          // ===== QUALITY & SATISFACTION =====
          aboutCard([
            aboutHeading(elakayAutoText("h_about_quality_title", "Product Quality Commitment")),
            aboutText(elakayAutoText("h_about_quality_p1", "Quality is one of our highest priorities.")),
            aboutText(elakayAutoText("h_about_quality_p2", "We work to ensure that products are accurately represented through descriptions, specifications, images, and other information provided within our listings.")),
            aboutText(elakayAutoText("h_about_quality_p3", "Our team continually reviews products and inventory to help maintain consistency and customer satisfaction. We encourage customers to review product details carefully and contact us whenever additional information is required.")),
            aboutHeading(elakayAutoText("h_about_satisfaction_title", "Customer Satisfaction"), { tag: "h3", size: "20px" }),
            aboutText(elakayAutoText("h_about_satisfaction_p1", "Customer satisfaction is at the center of everything we do. We are committed to providing responsive communication and professional support before, during, and after every purchase.")),
            aboutText(elakayAutoText("h_about_satisfaction_list", "Our customer service philosophy is based on:\n• Respect\n• Professionalism\n• Transparency\n• Responsiveness\n• Continuous improvement")),
            aboutText(elakayAutoText("h_about_satisfaction_p2", "Whenever issues arise, we strive to find fair and reasonable solutions."), { margin: 0 }),
          ]),

          // ===== HOW WE WORK (warehouse, packaging, shipping, support) =====
          aboutCard([
            aboutHeading(elakayAutoText("h_about_ops_title", "How We Work")),
            aboutGalleryRow([
              elakayAutoText("h_about_gal_warehouse", "Warehouse / Inventory"),
              elakayAutoText("h_about_gal_packaging", "Packaging Process"),
              elakayAutoText("h_about_gal_shipping", "Shipping & Delivery"),
              elakayAutoText("h_about_gal_support", "Customer Support"),
            ], { ratio: "1 / 1" }),
            aboutHeading(elakayAutoText("h_about_orderproc_title", "Order Processing"), { tag: "h3", size: "20px" }),
            aboutText(elakayAutoText("h_about_orderproc_p1", "Every order is carefully reviewed and processed.")),
            aboutText(elakayAutoText("h_about_orderproc_list", "Our order preparation process may include:\n• Inventory verification\n• Product inspection\n• Packaging preparation\n• Shipping coordination\n• Tracking updates")),
            aboutText(elakayAutoText("h_about_orderproc_p2", "Processing times may vary depending on product availability, order volume, holidays, and shipping destinations. Our objective is always to prepare orders as efficiently as possible.")),
            aboutHeading(elakayAutoText("h_about_shipping_title", "Shipping and Delivery"), { tag: "h3", size: "20px" }),
            aboutText(elakayAutoText("h_about_shipping_p1", "We work to provide reliable shipping services and delivery solutions whenever available.")),
            aboutText(elakayAutoText("h_about_shipping_list", "Customers may receive:\n• Order confirmation\n• Shipping confirmation\n• Tracking information\n• Delivery notifications")),
            aboutText(elakayAutoText("h_about_shipping_p2", "Delivery times may vary based on location, carrier services, weather conditions, customs procedures, and other external factors beyond our direct control. We appreciate our customers' patience and understanding during the delivery process.")),
            aboutHeading(elakayAutoText("h_about_comm_title", "Communication"), { tag: "h3", size: "20px" }),
            aboutText(elakayAutoText("h_about_comm_p1", "Clear communication is one of our priorities.")),
            aboutText(elakayAutoText("h_about_comm_list", "We encourage customers to contact us whenever they have questions regarding:\n• Product information\n• Product availability\n• Order status\n• Shipping updates\n• Returns and exchanges\n• General inquiries")),
            aboutText(elakayAutoText("h_about_comm_p2", "We strive to respond as quickly as possible and provide helpful information to support our customers."), { margin: 0 }),
          ]),

          // ===== WHY SHOP WITH US (advantages) =====
          aboutCard([
            aboutImage(elakayAutoText("h_about_gal_advantages", "Why Shop With Us / Our Advantages"), { ratio: "16 / 5", mb: "20px" }),
            aboutHeading(elakayAutoText("h_about_advantages_title", "Why Shop With Us")),
            aboutBadgeGrid(advantages),
          ]),

          // ===== SECURE SHOPPING =====
          aboutCard([
            aboutImage(elakayAutoText("h_about_gal_secure", "Secure Shopping"), { bg: "0f172a", fg: "38bdf8", ratio: "16 / 6", mb: "20px" }),
            aboutHeading(elakayAutoText("h_about_secure_title", "Secure Shopping Experience")),
            aboutText(elakayAutoText("h_about_secure_p1", "Shopping security is extremely important.")),
            aboutText(elakayAutoText("h_about_secure_p2", "Orders placed through Elakay benefit from secure payment processing and marketplace protection systems designed to help create a safer environment for both buyers and sellers."), { margin: 0 }),
          ]),

          // ===== WORLDWIDE SHIPPING =====
          aboutCard([
            aboutImage(elakayAutoText("h_about_gal_worldwide", "Worldwide Shipping / Service Areas"), { bg: "1d4ed8", fg: "ffffff", ratio: "16 / 7", mb: "16px" }),
            aboutText(elakayAutoText("h_about_worldwide_p1", "We work to provide reliable shipping services and delivery solutions wherever our customers are located, keeping them informed every step of the way."), { margin: 0 }),
          ]),

          // ===== CUSTOMER REVIEWS =====
          aboutCard([
            aboutImage(elakayAutoText("h_about_gal_reviews", "Customer Reviews"), { ratio: "16 / 9", mb: "16px" }),
            {
              id: makeNodeId("about-rating"),
              type: "rating",
              content: { value: 4.8, max: 5, color: "#f59e0b", label: elakayAutoText("h_about_reviews_label", "4.8 out of 5 - based on 2,500 reviews") },
              style: { display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" },
            },
          ]),

          // ===== RETURNS & RESOLUTION =====
          aboutCard([
            aboutHeading(elakayAutoText("h_about_returns_title", "Returns and Resolution Process")),
            aboutText(elakayAutoText("h_about_returns_p1", "Customer confidence is important to us. If a customer experiences an issue with an order, we encourage them to contact us promptly through the available communication channels.")),
            aboutText(elakayAutoText("h_about_returns_list", "Depending on marketplace policies and specific circumstances, customers may be eligible for assistance regarding:\n• Product concerns\n• Damaged items\n• Incorrect items\n• Missing items\n• Return requests\n• Replacement requests")),
            aboutText(elakayAutoText("h_about_returns_p2", "Our goal is always to work toward a fair and satisfactory resolution whenever possible."), { margin: 0 }),
          ]),

          // ===== CONTACT INFORMATION =====
          aboutCard([
            aboutImage(elakayAutoText("h_about_gal_contact", "Contact Information"), { bg: "f1f5f9", fg: "0f172a", ratio: "16 / 6", mb: "20px" }),
            aboutHeading(elakayAutoText("h_about_contact_title", "Contact Information")),
            aboutBadgeGrid(contactItems),
          ]),

          // ===== PAYMENT METHODS =====
          aboutCard([
            aboutImage(elakayAutoText("h_about_gal_payment", "We Accept - Secure Payment Methods"), { bg: "f8fafc", fg: "0f172a", ratio: "16 / 5", mb: "20px" }),
            {
              id: makeNodeId("about-payment-row"),
              type: "row",
              style: { display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "10px", marginBottom: "12px" },
              children: paymentMethods.map((label) => ({
                id: makeNodeId("about-payment-badge"),
                type: "text",
                tag: "span",
                text: label,
                style: {
                  padding: "8px 18px",
                  borderRadius: "8px",
                  background: "#f1f5f9",
                  border: "1px solid #e2e8f0",
                  color: "#1f2937",
                  fontWeight: "700",
                  fontSize: "13px",
                },
              })),
            },
            aboutText(elakayAutoText("h_about_payment_text", "Your payment information is always safe with us."), { margin: 0, textAlign: "center" }),
          ]),

          // ===== OUR VALUES =====
          aboutCard([
            aboutImage(elakayAutoText("h_about_gal_values", "Our Values"), { bg: "eef2ff", fg: "4338ca", ratio: "16 / 7", mb: "20px" }),
            aboutHeading(elakayAutoText("h_about_values_title", "Our Values")),
            aboutBadgeGrid(values),
          ]),

          // ===== COMMUNITY SUPPORT =====
          aboutCard([
            aboutImage(elakayAutoText("h_about_gal_community", "Community Support"), { bg: "166534", fg: "ffffff", ratio: "16 / 7", mb: "16px" }),
            aboutHeading(elakayAutoText("h_about_community_title", "Supporting Communities")),
            aboutText(elakayAutoText("h_about_community_p1", "We believe that commerce can help strengthen communities and create opportunities for entrepreneurs, creators, producers, and small businesses.")),
            aboutText(elakayAutoText("h_about_community_p2", "By participating in the Elakay marketplace, we contribute to a growing network of individuals and businesses working together to create economic opportunities and sustainable growth."), { margin: 0 }),
          ]),

          // ===== SATISFACTION GUARANTEE =====
          aboutCard([
            aboutImage(elakayAutoText("h_about_gal_guarantee", "100% Satisfaction Guarantee"), { bg: "fef9c3", fg: "92400e", ratio: "16 / 7", mb: "16px" }),
            aboutHeading(elakayAutoText("h_about_guarantee_title", "Your Satisfaction Is Our Priority")),
            aboutText(elakayAutoText("h_about_guarantee_p1", "We are committed to providing the best products and service. If you are not satisfied, we will make it right."), { margin: 0 }),
          ]),

          // ===== THANK YOU / CTA FOOTER =====
          {
            id: makeNodeId("about-cta-footer"),
            type: "container",
            style: {
              padding: "48px 32px",
              borderRadius: "20px",
              textAlign: "center",
              color: "#ffffff",
              background: "linear-gradient(135deg,#075985,#6d28d9)",
            },
            children: [
              aboutHeading(elakayAutoText("h_about_thankyou_title", "Thank You"), { tag: "h2", color: "#ffffff", size: "30px", align: "center", style: { fontStyle: "italic" } }),
              aboutText(elakayAutoText("h_about_thankyou_p1", "Thank you for visiting {value0}. We appreciate your trust and look forward to serving you again.", { value0: shopName }), { color: "#e0f2fe", textAlign: "center", maxWidth: "640px", margin: "0 auto 8px" }),
              {
                id: makeNodeId("about-thankyou-stars"),
                type: "text",
                tag: "div",
                text: "★★★★★",
                style: { fontSize: "22px", color: "#fbbf24", letterSpacing: "4px", margin: "8px 0 24px" },
              },
              aboutText(elakayAutoText("h_about_thankyou_p2", "Thank you for choosing our shop. Every customer is important to us, and we look forward to serving you with professionalism, integrity, and dedication."), { color: "#e0f2fe", textAlign: "center", maxWidth: "640px", margin: "0 auto 26px" }),
              {
                id: makeNodeId("about-cta-footer-btn"),
                type: "button",
                text: elakayAutoText("h_about_cta_visit2", "Visit our shop"),
                href: shopHref,
                style: {
                  display: "inline-block",
                  padding: "13px 32px",
                  borderRadius: "999px",
                  background: "#ffffff",
                  color: "#075985",
                  fontWeight: "800",
                  fontSize: "14px",
                  textDecoration: "none",
                },
              },
            ],
          },
        ],
      },
    ],
  };
};

const getAboutPage = (pages) =>
  (Array.isArray(pages) && pages.find((page) => page?.slug === "about")) ||
  (Array.isArray(pages) ? pages[0] : null);

export default function ShopAboutPage({ infoSource }) {
  const { id: userID } = useParams();
  // Exact boutique of the shop website (?shop=<User.boutiques._id>): its name
  // and links. The About content itself belongs to the account.
  const shopId = new URLSearchParams(useLocation().search).get("shop") || null;
  const [owner, setOwner] = useState(null);
  const [boutique, setBoutique] = useState(null);
  const [aboutPage, setAboutPage] = useState(null);
  const [doc, setDoc] = useState(null);
  const [editing, setEditing] = useState(false);
  const [filterShop, setFilterShop] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savedMessage, setSavedMessage] = useState("");

  const session = isAuthenticated() || {};
  const currentUserId =
    infoSource?.userInfo?._id ||
    session?.user?._id ||
    session?._id ||
    null;
  // userID (param d'URL) peut etre un publicId (ex: "u-8Kx42Z") : on compare en
  // priorite au vrai _id du owner deja resolu par getUserAbout cote backend,
  // et on ne retombe sur userID que tant que owner n'est pas encore charge.
  const isOwner = Boolean(
    currentUserId &&
      (String(currentUserId) === String(owner?._id) || String(currentUserId) === String(userID))
  );

  const loadAbout = useCallback(async () => {
    if (!userID) return;
    setLoading(true);
    setError("");
    try {
      const [data, publicShop] = await Promise.all([
        getUserAbout(userID),
        shopId ? getPublicShop(shopId).then((r) => r?.shop || null).catch(() => null) : null,
      ]);
      // Only a boutique of THIS owner is used (never another account's).
      const ownBoutique = publicShop && String(publicShop.owner?._id || publicShop.owner) === String(data?.owner?._id) ? publicShop : null;
      const page = getAboutPage(data?.about);
      const nextDoc = page?.doc?.pages?.length
        ? page.doc
        : makeStarterAboutDoc(data?.owner, page?.legacyText || page?.description || "", ownBoutique);
      setOwner(data?.owner || null);
      setBoutique(ownBoutique);
      setAboutPage(page || {
        slug: "about",
        title: nextDoc.meta?.title || "About Us",
        description: nextDoc.meta?.description || "",
        status: "published",
        pageMode: nextDoc.pageMode || "static",
      });
      setDoc(nextDoc);
    } catch (requestError) {
      setError(requestError.message || elakayAutoText("h_10a8dd85d3953f", "Impossible de charger cette page."));
    } finally {
      setLoading(false);
    }
  }, [userID, shopId]);

  useEffect(() => {
    loadAbout();
  }, [loadAbout]);

  const shopName = useMemo(
    () => boutique?.name || owner?.shopName || owner?.firstname || "Seller",
    [owner, boutique]
  );
  const shopHomePath = `/lakay-ShopSeller/${getPublicIdentifier(owner, userID)}${boutique?._id ? `?shop=${boutique._id}` : ""}`;

  const renderShopHeader = useCallback((drawerClickHander) => (
    <HeaderShopSeller
      infoSource={infoSource}
      userID={getPublicIdentifier(owner, userID)}
      shopPath={shopHomePath}
      shopName={shopName}
      loadindSearCh={() => {}}
      onFilterShop={() => {}}
      search=""
      drawerClickHander={drawerClickHander}
      outOffPage={false}
      showFilter={false}
      shopType="seller"
      canManageShop={isOwner}
    />
  ), [infoSource, shopName, userID, owner, isOwner, shopHomePath]);

  const handleSave = useCallback(async (payload) => {
    const result = await saveUserAbout({
      ...aboutPage,
      slug: "about",
      title: payload.title || "About Us",
      description: payload.description || "",
      status: payload.status || "published",
      pageMode: payload.pageMode || payload.doc?.pageMode || "static",
      doc: payload.doc,
    });
    const savedPage = result?.page;
    if (savedPage) {
      setAboutPage(savedPage);
      setDoc(savedPage.doc);
      setSavedMessage("Page About Us sauvegardee.");
      setTimeout(() => setSavedMessage(""), 3000);
    }
    return { ...savedPage, owner: userID };
  }, [aboutPage, userID]);

  if (loading) {
    return <div style={{ padding: "80px 24px", textAlign: "center", color: "#6b7280" }}>{elakayAutoText("h_3aa79ccaa96707", "Chargement de la page About Us...")}</div>;
  }

  if (error) {
    return (
      <div style={{ padding: "80px 24px", textAlign: "center" }}>
        <p style={{ color: "#b91c1c" }}>{error}</p>
        <Link to={shopHomePath}>{elakayAutoText("h_5ff8ac27909790", "Retour a la boutique")}</Link>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
              <Layout
                  {...infoSource?.allSettings?.layout}
                  className="container-fluid content"
                  loading={loading}
                  infoSource ={infoSource}
                  SliderPunShow={false}
                  filterShop={filterShop}
                  setFilterShop={setFilterShop}
                  userShop= {true}
                  outOffPage = {true}
                  allHeaderShop={renderShopHeader}
                  inpage={elakayAutoText("h_5780daf6db0b01", "about")}
                  showFilterMenu={false}
              >
      {isOwner && (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "10px",
          padding: "10px 18px",
          background: "#ffffff",
          borderBottom: "1px solid #e5e7eb",
        }}>
          {savedMessage && <span style={{ color: "#15803d", fontSize: "12px", fontWeight: 700 }}>{savedMessage}</span>}
          <button
            type="button"
            onClick={() => setEditing((value) => !value)}
            style={{
              padding: "8px 14px",
              border: "none",
              borderRadius: "9px",
              background: editing ? "#0f766e" : "#6d28d9",
              color: "#ffffff",
              fontSize: "12px",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            {editing ? elakayAutoText("h_825613483be15b", "Voir la page") : elakayAutoText("h_b96c5c861bac45", "Modifier dans PageBuilder")}
          </button>
        </div>
      )}

      {editing && isOwner ? (
        <PageBuilder
          key={`${userID}-${aboutPage?._id || "about"}`}
          initialDoc={doc}
          pageMode={doc?.pageMode || "static"}
          onDocChange={setDoc}
          onSave={handleSave}
          savedPageId={aboutPage?._id || "about"}
          savedPage={{ ...aboutPage, owner: userID }}
          infoSource={infoSource}
          currentUserId={currentUserId}
          fitParentHeight={false}
        />
      ) : (
        <PageRenderer
          doc={doc}
          pageData={null}
          infoSource={infoSource}
          token={session?.token}
          device="desktop"
        />
      )}
      </Layout>
    </div>
  );
}
